import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Board } from '@/lib/api/types';
import { api, req } from '@/lib/api/client';
import { keys } from '@/lib/api/keys';
import { useSessionStore } from '@/stores/session';
import { toast } from '@/stores/ui';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Input, Label } from '@/components/Input';
import { isApiError } from '@/lib/api/errors';
import { BoardPickerDialog } from './BoardPickerDialog';
// --- release2/B ---
import { ListChecks } from 'lucide-react';
import { usePermission } from '@/features/workspace/usePermission';
import { FieldPoliciesDialog } from '@/features/field-policies/FieldPoliciesDialog';

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', null];

function invalidateBoardLists(qc: ReturnType<typeof useQueryClient>, b: Pick<Board, 'library_id' | 'parent_board_id'>) {
  void qc.invalidateQueries({ queryKey: keys.libraryBoards(b.library_id) });
  if (b.parent_board_id) {
    void qc.invalidateQueries({ queryKey: keys.boardChildren(b.parent_board_id) });
    void qc.invalidateQueries({ queryKey: keys.board(b.parent_board_id) });
  }
}

export function CreateBoardDialog({ parent, libraryId, onClose }: { parent: Board | null; libraryId: string; onClose: () => void }) {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const { ws = '' } = useParams();
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const m = useMutation({
    mutationFn: () => req(api.POST('/workspaces/{workspaceId}/boards', { params: { path: { workspaceId: wsId } }, body: { library_id: libraryId, parent_board_id: parent?.id ?? undefined, name: name.trim(), color: color ?? undefined } })),
    onSuccess: (b) => {
      invalidateBoardLists(qc, b);
      onClose();
      navigate(`/w/${ws}/b/${b.id}`);
    },
    onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not create board' }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) m.mutate();
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={parent ? `New board in ${parent.name}` : 'New board'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" form="create-board" type="submit" disabled={!name.trim() || m.isPending}>
            Create
          </Button>
        </>
      }
    >
      <form id="create-board" onSubmit={submit} className="space-y-3">
        <div>
          <Label htmlFor="board-name">Name</Label>
          <Input id="board-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required />
        </div>
        <ColorPicker value={color} onChange={setColor} />
      </form>
    </Dialog>
  );
}

export function ColorPicker({ value, onChange }: { value: string | null; onChange: (c: string | null) => void }) {
  return (
    <div>
      <Label>Colour</Label>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Board colour">
        {COLORS.map((c) => (
          <button key={c ?? 'none'} type="button" role="radio" aria-checked={value === c} aria-label={c ?? 'No colour'} onClick={() => onChange(c)} className="h-6 w-6 rounded-full border border-line-strong" style={{ background: c ?? 'transparent', outline: value === c ? '2px solid var(--lm-accent)' : undefined, outlineOffset: 2 }} />
        ))}
      </div>
    </div>
  );
}

export function RenameBoardDialog({ board, onClose }: { board: Board; onClose: () => void }) {
  const [name, setName] = useState(board.name);
  const [color, setColor] = useState<string | null>(board.color ?? null);
  const [description, setDescription] = useState(board.description ?? '');
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => req(api.PATCH('/boards/{boardId}', { params: { path: { boardId: board.id } }, body: { name: name.trim(), color, description } })),
    onSuccess: (b) => {
      qc.setQueryData(keys.board(board.id), b);
      invalidateBoardLists(qc, b);
      onClose();
    },
    onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not update board' }),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title="Edit board"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => m.mutate()} disabled={!name.trim() || m.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="rename">Name</Label>
          <Input id="rename" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="desc">Description</Label>
          <Input id="desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <ColorPicker value={color} onChange={setColor} />
        <BoardFieldsEntry board={board} variant="link" />{/* release2/B */}
        <BoardDefaultsEntry board={board} variant="link" />{/* release3/B */}
        <BoardRetentionEntry board={board} /> {/* release2/C */}
        <BoardVisibilityEntry board={board} /> {/* release3/D */}
        <BoardGuestsEntry board={board} variant="link" /> {/* release3/D */}
      </div>
    </Dialog>
  );
}

export function MoveBoardDialog({ board, onClose }: { board: Board; onClose: () => void }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (t: { boardId: string | null; libraryId: string }) => req(api.POST('/boards/{boardId}/move', { params: { path: { boardId: board.id } }, body: { parent_board_id: t.boardId ?? undefined, library_id: t.libraryId } })),
    onSuccess: (b) => {
      invalidateBoardLists(qc, board);
      invalidateBoardLists(qc, b);
      qc.setQueryData(keys.board(board.id), b);
      toast({ kind: 'success', title: `Moved “${b.name}”` });
      onClose();
    },
    onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not move board' }),
  });
  return <BoardPickerDialog title={`Move “${board.name}” to…`} confirmLabel="Move here" exclude={[board.id]} allowLibraryRoot onPick={(t) => m.mutate(t)} onClose={onClose} />;
}

export function DeleteBoardDialog({ board, onClose }: { board: Board; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { ws = '' } = useParams();
  const m = useMutation({
    mutationFn: () => req(api.DELETE('/boards/{boardId}', { params: { path: { boardId: board.id } } })),
    onSuccess: () => {
      invalidateBoardLists(qc, board);
      qc.removeQueries({ queryKey: keys.board(board.id) });
      toast({ kind: 'success', title: `Deleted “${board.name}”` });
      onClose();
      navigate(board.parent_board_id ? `/w/${ws}/b/${board.parent_board_id}` : `/w/${ws}/l/${board.library_id}`);
    },
    onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not delete board' }),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title="Delete board"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={() => m.mutate()} disabled={m.isPending}>
            Delete
          </Button>
        </>
      }
    >
      <p>
        Delete <strong>{board.name}</strong>
        {board.child_board_count > 0 ? ` and its ${board.child_board_count} sub-boards` : ''}? Assets stay in the library and can be restored for 30 days.
      </p>
    </Dialog>
  );
}

// --- release2/B --- board settings › Fields (G3 field policies)
/** Opens the board's Fields tab; `link` sits inside the Edit board dialog, `button` in the board header. */
export function BoardFieldsEntry({ board, variant = 'button' }: { board: Board; variant?: 'button' | 'link' }) {
  const [open, setOpen] = useState(false);
  const canManage = usePermission('MANAGE_BOARD', { boardId: board.id });
  if (!canManage || board.kind === 'smart') return null;
  return (
    <>
      {variant === 'link' ? (
        <div>
          <Label>Fields</Label>
          <Button size="sm" onClick={() => setOpen(true)} data-board-fields>
            <ListChecks size={13} /> Required fields and defaults…
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)} title="Required fields and defaults for this board" data-board-fields>
          <ListChecks size={13} /> Fields
        </Button>
      )}
      {open && <FieldPoliciesDialog board={board} onClose={() => setOpen(false)} />}
    </>
  );
}

// --- release2/C --- board settings › Retention (S7b)
import { Archive } from 'lucide-react';
import { RetentionDialog } from '@/features/retention/RetentionDialog';
/** Opens the board's Retention tab (MANAGE_BOARD; smart boards carry no policy). */
export function BoardRetentionEntry({ board }: { board: Board }) {
  const [open, setOpen] = useState(false);
  const canManage = usePermission('MANAGE_BOARD', { boardId: board.id });
  if (!canManage || board.kind === 'smart') return null;
  return (
    <div>
      <Label>Retention</Label>
      <Button size="sm" onClick={() => setOpen(true)} data-board-retention>
        <Archive size={13} /> {board.has_retention ? 'Edit retention policy…' : 'Retention policy…'}
      </Button>
      {open && <RetentionDialog board={board} onClose={() => setOpen(false)} />}
    </div>
  );
}

// --- release3/D --- board settings › Visibility (S2 private boards). Invite people (DEL-2 board guests) joins in phase 2.
import { BoardVisibilityControl } from '@/features/board-guests/BoardVisibilityControl';
/** Workspace / private switch inside the Edit board dialog (the guard already required MANAGE_BOARD to open it). */
export function BoardVisibilityEntry({ board }: { board: Board }) {
  return <BoardVisibilityControl board={board} />;
}

import { UserPlus } from 'lucide-react';
import { BoardShareDialog } from '@/features/board-guests/BoardShareDialog';
/** Opens the board's Share dialog (visibility + invited people); `link` sits in the Edit board dialog, `button` in the header. MANAGE_BOARD; smart boards host no guests. */
export function BoardGuestsEntry({ board, variant = 'button' }: { board: Board; variant?: 'button' | 'link' }) {
  const [open, setOpen] = useState(false);
  const canManage = usePermission('MANAGE_BOARD', { boardId: board.id });
  if (!canManage || board.kind === 'smart') return null;
  return (
    <>
      {variant === 'link' ? (
        <div>
          <Label>People</Label>
          <Button size="sm" onClick={() => setOpen(true)} data-board-share>
            <UserPlus size={13} /> Invite people…
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)} title="Who can see this board and who is invited" data-board-share>
          <UserPlus size={13} /> Share
        </Button>
      )}
      {open && <BoardShareDialog board={board} onClose={() => setOpen(false)} />}
    </>
  );
}

// --- release3/B --- board settings › Defaults (META-2: default tags + approval applied when assets enter the board)
import { Wand2 } from 'lucide-react';
import { BoardDefaultsDialog } from '@/features/board-defaults/BoardDefaultsDialog';
/** Opens the board's Defaults tab; `link` sits inside the Edit board dialog, `button` in the board header. Smart boards hold no assets, so no defaults. */
export function BoardDefaultsEntry({ board, variant = 'button' }: { board: Board; variant?: 'button' | 'link' }) {
  const [open, setOpen] = useState(false);
  const canManage = usePermission('MANAGE_BOARD', { boardId: board.id });
  if (!canManage || board.kind === 'smart') return null;
  return (
    <>
      {variant === 'link' ? (
        <div>
          <Label>Defaults</Label>
          <Button size="sm" onClick={() => setOpen(true)} data-board-defaults>
            <Wand2 size={13} /> Default tags and approval…
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)} title="Tags and approval status applied to assets that enter this board" data-board-defaults>
          <Wand2 size={13} /> Defaults
        </Button>
      )}
      {open && <BoardDefaultsDialog board={board} onClose={() => setOpen(false)} />}
    </>
  );
}
